import { SelecteurLangue } from '../components/SelecteurLangue'
import { useTextePublic } from '../lib/textes-publics'
/**
 * Connexion au portail abonné — **sans mot de passe** (POR-01, D10).
 *
 * L’abonné saisit son email, reçoit un lien valable 15 minutes à usage unique,
 * puis reste connecté 30 jours sur l’appareil.
 *
 * Deux règles de discrétion, tenues ici comme côté serveur :
 *  - la réponse est **la même** que l’adresse existe ou non (pas d’énumération
 *    des abonnés du théâtre) ;
 *  - au-delà de trois demandes par heure, le serveur refuse (A-02) et son
 *    message est affiché tel quel.
 */

import { type FormEvent, useState } from 'react'

import { Link, Navigate } from 'react-router'

import { messageErreur } from '../lib/api'
import { CHEMINS } from '../lib/chemins'
import { useTheatre } from '../lib/theatre'
import { questionPersonnel, titreEspaceAbonne, useLexique } from '../lib/vocabulaire'
import { Button } from '../ui/Button'
import { BandeauErreur } from '../ui/EmptyState'
import { FormField } from '../ui/FormField'
import { Input } from '../ui/Input'
import { ChargementPage } from '../ui/Spinner'
import { useAuth } from './useAuth'

export function ConnexionAbonne() {
  const texteInterface = useTextePublic()

  const { session, chargement, demanderLienMagique } = useAuth()
  /* `GET /theatre/publique` — sans session : l'abonné doit lire le nom de SON
     théâtre avant d'avoir demandé quoi que ce soit. */
  const { nom: nomTheatre, logo, type: typeTenant } = useTheatre()
  /* ★ EVT-04 — chez un organisateur d'événement ponctuel il n'y a pas d'abonné :
     c'est l'espace d'une personne, et l'adresse a été communiquée à
     l'organisateur, pas « au théâtre » (`lib/vocabulaire.ts`). */
  const mots = useLexique(texteInterface.langue)

  const [email, setEmail] = useState('')
  const [envoye, setEnvoye] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  if (chargement) return <ChargementPage libelle={texteInterface('Vérification de la session…')} />
  if (session?.sujet === 'abonne') return <Navigate to={CHEMINS.portail} replace />

  async function soumettre(evenement: FormEvent): Promise<void> {
    evenement.preventDefault()
    setErreur(null)
    setEnCours(true)
    try {
      await demanderLienMagique(email.trim())
      setEnvoye(true)
    } catch (cause) {
      setErreur(messageErreur(cause, texteInterface('Impossible d’envoyer le lien de connexion.')))
    } finally {
      setEnCours(false)
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-slate-100 px-4 py-12">
      <div className="w-full max-w-md">
        <header className="mb-6 text-center">
          {logo && (
            <img
              src={logo}
              alt={nomTheatre}
              className="mx-auto mb-4 max-h-20 max-w-48 object-contain"
            />
          )}
          <p className="text-sm font-medium tracking-wide text-rideau-800 uppercase">
            {nomTheatre}
          </p>
          <h1 className="mt-1 text-xl font-semibold text-slate-900">
            {titreEspaceAbonne(typeTenant, texteInterface.langue)}
          </h1>
          <SelecteurLangue className="mt-3 text-slate-700" />
        </header>

        {envoye ? (
          <div className="rounded-carte border border-slate-200 bg-white p-6 text-center shadow-sm">
            <span className="mx-auto flex size-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                className="size-6"
                aria-hidden="true"
              >
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3.5 7 8.5 6 8.5-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <h2 className="mt-4 text-base font-semibold text-slate-900">
              {texteInterface('Vérifiez vos emails')}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {texteInterface(
                'Si l’adresse {email} est connue, un lien de connexion vient d’y être envoyé. Il est valable 15 minutes et ne fonctionne qu’une fois.',
                { email: email.trim() },
              )}
            </p>
            <p className="mt-3 text-sm text-slate-500">
              {texteInterface('Pensez à regarder dans les indésirables.')}
            </p>
            <Button
              variante="secondaire"
              className="mt-5"
              onClick={() => {
                setEnvoye(false)
                setErreur(null)
              }}
            >
              {' '}
              {texteInterface('Utiliser une autre adresse')}{' '}
            </Button>
          </div>
        ) : (
          <form
            onSubmit={(evenement) => {
              void soumettre(evenement)
            }}
            className="flex flex-col gap-4 rounded-carte border border-slate-200 bg-white p-6 shadow-sm"
            noValidate
          >
            <p className="text-sm text-slate-600">
              {texteInterface(
                'Saisissez l’adresse email communiquée {exploitant}. Vous recevrez un lien de connexion : ni mot de passe à créer, ni mot de passe à retenir.',
                { exploitant: mots.auExploitant },
              )}
            </p>

            {erreur !== null && <BandeauErreur message={erreur} />}

            <FormField label={texteInterface('Adresse email')} requis>
              {(champ) => (
                <Input
                  {...champ}
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(evenement) => {
                    setEmail(evenement.target.value)
                  }}
                  // Placeholder volontairement générique : cette page est
                  // publique, y afficher une adresse nominative plausible
                  // reviendrait à souffler un compte réel (POR-01).
                  placeholder={texteInterface('prenom.nom@exemple.ch')}
                />
              )}
            </FormField>

            <Button type="submit" chargement={enCours} pleineLargeur taille="lg">
              {' '}
              {texteInterface('Recevoir mon lien de connexion')}{' '}
            </Button>
          </form>
        )}

        <p className="mt-6 text-center text-xs text-slate-500">
          {texteInterface(
            'Pas d’email chez {exploitant}, ou adresse à corriger ? Contactez le guichet. Les corrections de coordonnées et les changements de place se font par {responsable}.',
            { exploitant: mots.leExploitant, responsable: mots.leExploitant },
          )}
        </p>

        {/* Le personnel qui tape simplement l’adresse du site atterrit ici :
            son entrée doit être visible et atteignable au doigt, pas une note
            de bas de page. */}
        <div className="mt-6 border-t border-slate-200 pt-5 text-center">
          <p className="text-sm text-slate-600">
            {questionPersonnel(typeTenant, texteInterface.langue)}
          </p>
          <Link
            to={CHEMINS.connexionStaff}
            className="mt-2 inline-flex min-h-11 items-center justify-center rounded-md px-4 text-sm font-medium text-rideau-800 underline underline-offset-4 hover:bg-white"
          >
            {' '}
            {texteInterface('Connexion du personnel (back-office)')}{' '}
          </Link>
        </div>
      </div>
    </main>
  )
}
